import React, { useState } from 'react';
import { PhotoEntity, useFetchUserPublicProfileQuery } from 'src/store/userApi';
import { Button } from 'src/ui/Button';
import { EmptyState } from 'src/ui/EmptyState';
import { Icon, IconName } from 'src/ui/Icon';
import { LoadingBlock } from 'src/ui/Spinner';

type Lang = 'zh' | 'en';

const LANG_STORAGE_KEY = 'publicProfileLang';

const C = {
  paper: '#FFFFFF',
  paperWarm: '#F5F2F0',
  ink: '#1A170F',
  inkMuted: '#5C5C5C',
  amberDeep: '#936A00',
  line: '#EEEEEE',
};

const FONT =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'PingFang SC', 'Helvetica Neue', sans-serif";

const HERO_HEIGHT = 520;
const PHOTO_HEIGHT = 320;
const FRAME_WIDTH = 390;
const FRAME_HEIGHT = 844;
const HERO_FALLBACK_BG = 'linear-gradient(135deg, #D6B07A, #B5824E, #5C3F25)';

const LABELS: Record<
  Lang,
  {
    factorAge: string;
    factorOccupation: string;
    factorEducation: string;
    basics: string;
    lifestyle: string;
    aboutWithName: (name: string) => string;
    aboutGeneric: string;
    zodiac: Record<string, string>;
    chineseZodiac: Record<string, string>;
  }
> = {
  zh: {
    factorAge: '年龄',
    factorOccupation: '职业',
    factorEducation: '教育背景',
    basics: '基本信息',
    lifestyle: '生活方式',
    aboutWithName: (name) => `关于 ${name}`,
    aboutGeneric: '关于',
    zodiac: {
      aries: '白羊座',
      taurus: '金牛座',
      gemini: '双子座',
      cancer: '巨蟹座',
      leo: '狮子座',
      virgo: '处女座',
      libra: '天秤座',
      scorpio: '天蝎座',
      sagittarius: '射手座',
      capricorn: '摩羯座',
      aquarius: '水瓶座',
      pisces: '双鱼座',
      unknown: '未知',
    },
    chineseZodiac: {
      rat: '鼠',
      ox: '牛',
      tiger: '虎',
      rabbit: '兔',
      dragon: '龙',
      snake: '蛇',
      horse: '马',
      goat: '羊',
      monkey: '猴',
      rooster: '鸡',
      dog: '狗',
      pig: '猪',
    },
  },
  en: {
    factorAge: 'AGE',
    factorOccupation: 'OCCUPATION',
    factorEducation: 'EDUCATION',
    basics: 'The basics',
    lifestyle: 'Lifestyle',
    aboutWithName: (name) => `About ${name}`,
    aboutGeneric: 'About',
    zodiac: {
      aries: 'Aries',
      taurus: 'Taurus',
      gemini: 'Gemini',
      cancer: 'Cancer',
      leo: 'Leo',
      virgo: 'Virgo',
      libra: 'Libra',
      scorpio: 'Scorpio',
      sagittarius: 'Sagittarius',
      capricorn: 'Capricorn',
      aquarius: 'Aquarius',
      pisces: 'Pisces',
      unknown: 'Unknown',
    },
    chineseZodiac: {
      rat: 'Rat',
      ox: 'Ox',
      tiger: 'Tiger',
      rabbit: 'Rabbit',
      dragon: 'Dragon',
      snake: 'Snake',
      horse: 'Horse',
      goat: 'Goat',
      monkey: 'Monkey',
      rooster: 'Rooster',
      dog: 'Dog',
      pig: 'Pig',
    },
  },
};

type DisplayProfile = {
  name?: string;
  age?: number;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  height?: number | string;
  zodiac?: string;
  chineseZodiac?: string;
  gender?: string;
  occupation?: string;
  education?: string;
  homeTown?: string;
  maritalStatus?: string;
  haveChildren?: string;
  smoking?: string;
  drinking?: string;
  drug?: string;
  ethnicity?: string;
  languages?: string;
  religion?: string;
  residentStatus?: string;
  summary?: string;
};

type ChipItem = { key: string; label: string; icon: IconName };

const readStoredLang = (): Lang => {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY) === 'en' ? 'en' : 'zh';
  } catch {
    return 'zh';
  }
};

const pickPhotoUrl = (photo?: PhotoEntity) =>
  photo?.lImage?.url || photo?.mImage?.url || photo?.sImage?.url || '';

const formatHeight = (h: unknown): string => {
  if (typeof h === 'string') return h;
  if (typeof h !== 'number' || !Number.isFinite(h)) return '';
  const totalInches = Math.round(h / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${h}cm (${feet}'${inches}'')`;
};

const formatLocation = (p?: DisplayProfile) =>
  [p?.city, p?.state, p?.country, p?.postalCode].filter(Boolean).join(', ');

interface UserPublicProfileProps {
  userId: string;
}

const UserPublicProfile = ({ userId }: UserPublicProfileProps) => {
  const [lang, setLang] = useState<Lang>(readStoredLang);
  const { data, isFetching, isError } = useFetchUserPublicProfileQuery(userId, {
    refetchOnMountOrArgChange: true,
  });

  const changeLang = (next: Lang) => {
    setLang(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      return;
    }
  };

  const user = data?.data?.user;

  let body: React.ReactNode;
  if (isFetching && !user) {
    body = <LoadingBlock label="Loading public profile…" />;
  } else if (isError || !data?.success) {
    body = (
      <EmptyState
        icon="alert-circle"
        title="Couldn't load the public profile"
        desc={data?.message}
      />
    );
  } else if (!user) {
    body = (
      <EmptyState
        icon="eye"
        title="No public profile"
        desc={data?.message || 'This user is deleted or has no profile yet.'}
      />
    );
  } else {
    body = (
      <PhoneFrame>
        <PublicProfileContent
          profile={user.profile as DisplayProfile | undefined}
          profileImage={(user as { profileImage?: PhotoEntity }).profileImage}
          photos={user.photos ?? []}
          lang={lang}
        />
      </PhoneFrame>
    );
  }

  return (
    <div
      className="col"
      style={{ height: '100%', overflow: 'auto', padding: 20, gap: 16 }}
    >
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          What other users see in the app. Hidden fields and private data are
          removed by the same server logic.
        </div>
        <div className="row" style={{ gap: 4 }}>
          <Button
            size="sm"
            variant={lang === 'zh' ? 'primary' : 'ghost'}
            onClick={() => changeLang('zh')}
          >
            中文
          </Button>
          <Button
            size="sm"
            variant={lang === 'en' ? 'primary' : 'ghost'}
            onClick={() => changeLang('en')}
          >
            EN
          </Button>
        </div>
      </div>
      {body}
    </div>
  );
};

export default UserPublicProfile;

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'relative',
        alignSelf: 'center',
        flexShrink: 0,
        width: FRAME_WIDTH,
        height: FRAME_HEIGHT,
        maxHeight: 'calc(100vh - 220px)',
        minHeight: 560,
        borderRadius: 40,
        border: '10px solid #1A170F',
        overflow: 'hidden',
        background: C.paper,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 14,
          left: 14,
          zIndex: 10,
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'rgba(0,0,0,0.3)',
          border: '0.5px solid rgba(255,255,255,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: C.paper,
        }}
      >
        <Icon name="chevron-left" size={20} />
      </div>
      <div style={{ height: '100%', overflowY: 'auto', fontFamily: FONT }}>
        {children}
      </div>
    </div>
  );
}

function PublicProfileContent({
  profile,
  profileImage,
  photos,
  lang,
}: {
  profile?: DisplayProfile;
  profileImage?: PhotoEntity;
  photos: PhotoEntity[];
  lang: Lang;
}) {
  const L = LABELS[lang];
  const displayName = profile?.name?.trim() || '';
  const heroPhotoUrl = pickPhotoUrl(profileImage) || pickPhotoUrl(photos[0]);
  const inlinePhotos = photos.slice(1, 6);
  const summary = profile?.summary?.trim();
  const aboutParagraphs = summary ? summary.split(/\n\s*\n/) : [];
  const subline = formatLocation(profile);

  const translate = (map: Record<string, string>, raw?: string) =>
    raw?.trim() ? map[raw] ?? raw : '';

  const factors = [
    typeof profile?.age === 'number'
      ? { key: 'age', value: `${profile.age}`, label: L.factorAge }
      : null,
    profile?.occupation?.trim()
      ? {
          key: 'occupation',
          value: profile.occupation,
          label: L.factorOccupation,
        }
      : null,
    profile?.education?.trim()
      ? {
          key: 'education',
          value: profile.education,
          label: L.factorEducation,
        }
      : null,
  ].filter(Boolean) as { key: string; value: string; label: string }[];

  const basics: ChipItem[] = [];
  const heightStr = formatHeight(profile?.height);
  if (heightStr)
    basics.push({ key: 'height', label: heightStr, icon: 'ruler' });
  if (profile?.gender?.trim())
    basics.push({
      key: 'gender',
      label: profile.gender,
      icon: 'venus-and-mars',
    });
  const z = translate(L.zodiac, profile?.zodiac);
  if (z) basics.push({ key: 'zodiac', label: z, icon: 'sparkles' });
  const cz = translate(L.chineseZodiac, profile?.chineseZodiac);
  if (cz) basics.push({ key: 'chineseZodiac', label: cz, icon: 'moon' });
  const home = profile?.homeTown?.trim();
  if (home) basics.push({ key: 'homeTown', label: home, icon: 'house' });
  if (profile?.ethnicity?.trim())
    basics.push({ key: 'ethnicity', label: profile.ethnicity, icon: 'globe' });
  if (profile?.languages?.trim())
    basics.push({
      key: 'languages',
      label: profile.languages,
      icon: 'languages',
    });
  if (profile?.religion?.trim())
    basics.push({ key: 'religion', label: profile.religion, icon: 'church' });
  if (profile?.residentStatus?.trim())
    basics.push({
      key: 'residentStatus',
      label: profile.residentStatus,
      icon: 'badge-check',
    });

  const lifestyle: ChipItem[] = [];
  if (profile?.maritalStatus?.trim())
    lifestyle.push({
      key: 'maritalStatus',
      label: profile.maritalStatus,
      icon: 'heart',
    });
  if (profile?.haveChildren?.trim())
    lifestyle.push({
      key: 'haveChildren',
      label: profile.haveChildren,
      icon: 'baby',
    });
  if (profile?.smoking?.trim())
    lifestyle.push({
      key: 'smoking',
      label: profile.smoking,
      icon: 'cigarette',
    });
  if (profile?.drinking?.trim())
    lifestyle.push({ key: 'drinking', label: profile.drinking, icon: 'wine' });
  if (profile?.drug?.trim())
    lifestyle.push({ key: 'drug', label: profile.drug, icon: 'pill' });

  return (
    <div style={{ paddingBottom: 48 }}>
      <div style={{ position: 'relative', height: HERO_HEIGHT }}>
        {heroPhotoUrl ? (
          <img
            src={heroPhotoUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: HERO_FALLBACK_BG,
            }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: 120,
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.36), rgba(0,0,0,0))',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 280,
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)',
          }}
        />
        {displayName ? (
          <div
            style={{
              position: 'absolute',
              left: 20,
              right: 20,
              bottom: 48,
              zIndex: 2,
            }}
          >
            <div
              style={{
                fontSize: 44,
                fontWeight: 700,
                color: C.paper,
                letterSpacing: -0.9,
                lineHeight: '46px',
                textShadow: '0 2px 16px rgba(0,0,0,0.45)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            {subline ? (
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.92)',
                  textShadow: '0 1px 6px rgba(0,0,0,0.4)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {subline}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {factors.length > 0 ? (
        <div
          style={{
            position: 'relative',
            zIndex: 4,
            marginTop: -32,
            marginLeft: 16,
            marginRight: 16,
            background: C.paper,
            borderRadius: 18,
            padding: '16px 6px',
            display: 'flex',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {factors.map((f, i) => (
            <div
              key={f.key}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0 6px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                borderRight:
                  i < factors.length - 1 ? `0.5px solid ${C.line}` : undefined,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  lineHeight: '18px',
                  fontWeight: 700,
                  color: C.ink,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {f.value}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: C.inkMuted,
                  letterSpacing: 0.8,
                }}
              >
                {f.label}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {inlinePhotos[0] ? (
        <PhotoBlock url={pickPhotoUrl(inlinePhotos[0])} />
      ) : null}

      {basics.length > 0 ? (
        <ChipSection title={L.basics} facts={basics} />
      ) : null}

      {aboutParagraphs[0] ? (
        <AboutBlock
          title={displayName ? L.aboutWithName(displayName) : L.aboutGeneric}
          body={aboutParagraphs[0]}
        />
      ) : null}

      {inlinePhotos[1] ? (
        <PhotoBlock url={pickPhotoUrl(inlinePhotos[1])} />
      ) : null}

      {aboutParagraphs[1] ? <AboutBlock body={aboutParagraphs[1]} /> : null}

      {inlinePhotos[2] ? (
        <PhotoBlock url={pickPhotoUrl(inlinePhotos[2])} />
      ) : null}

      {lifestyle.length > 0 ? (
        <ChipSection title={L.lifestyle} facts={lifestyle} />
      ) : null}

      {aboutParagraphs.slice(2).length > 0 ? (
        <AboutBlock body={aboutParagraphs.slice(2).join('\n\n')} />
      ) : null}

      {inlinePhotos[3] ? (
        <PhotoBlock url={pickPhotoUrl(inlinePhotos[3])} />
      ) : null}
      {inlinePhotos[4] ? (
        <PhotoBlock url={pickPhotoUrl(inlinePhotos[4])} />
      ) : null}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        fontSize: 17,
        lineHeight: '22px',
        fontWeight: 700,
        color: C.ink,
        marginBottom: 10,
      }}
    >
      {title}
    </div>
  );
}

function ChipSection({ title, facts }: { title: string; facts: ChipItem[] }) {
  return (
    <div style={{ padding: '24px 20px 0' }}>
      <SectionHeader title={title} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {facts.map(({ key, label, icon }) => (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 999,
              background: C.paperWarm,
            }}
          >
            <Icon name={icon} size={14} strokeWidth={1.8} color={C.amberDeep} />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AboutBlock({ title, body }: { title?: string; body: string }) {
  return (
    <div style={{ padding: '20px 20px 0' }}>
      {title ? <SectionHeader title={title} /> : null}
      <div
        style={{
          fontSize: 15,
          lineHeight: '25px',
          fontWeight: 400,
          color: C.ink,
          whiteSpace: 'pre-wrap',
        }}
      >
        {body}
      </div>
    </div>
  );
}

function PhotoBlock({ url }: { url: string }) {
  if (!url) return null;
  return (
    <div style={{ padding: '20px 20px 0' }}>
      <img
        src={url}
        alt=""
        style={{
          width: '100%',
          height: PHOTO_HEIGHT,
          borderRadius: 18,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </div>
  );
}
